/**
 * Wavlynq apps. Wavlynq itself is umbrella only — never an app.
 * Trio (live): Fryqnc, Pulsynq, MindLynq.
 * V2 six (coming soon): Ablelynq, Deafwav, Fullynq, Jadesynq, Openlynq, Spiritsynq.
 *
 * Logos are sourced from the public `email-assets` Cloud storage bucket so
 * the boss-uploaded brand artwork stays the single source of truth across
 * web, email, and share previews.
 */

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const STORAGE_BASE = `${SUPABASE_URL}/storage/v1/object/public/email-assets`;

/** Full wordmark (symbol + text) — sourced from Cloud storage. */
const logoUrl = (slug: string) => `${STORAGE_BASE}/${slug}_logo`;
/**
 * Symbol-only square thumbnail — pre-cropped from the wordmark and shipped
 * in /public so the card thumbnail is the actual brand mark, not a blind
 * center crop of the wordmark.
 */
const iconUrl = (slug: string) => `/brand-icons/${slug}_icon.png`;

/**
 * App identifier — all 9 apps are first-class across DB, edge fns, and UI.
 * `LiveAppId` is kept as an alias for `AppId` for backward compatibility.
 */
export type AppId =
  | "fryqnc"
  | "pulsynq"
  | "mindlynq"
  | "ablelynq"
  | "deafwav"
  | "fullynq"
  | "jadesynq"
  | "openlynq"
  | "spiritsynq";

export type LiveAppId = AppId;

export interface AppMeta {
  id: AppId;
  name: string;
  community: string;
  tagline: string;
  description: string;
  /** Full wordmark logo (used inside the card body). */
  logo: string;
  /** Same logo, used as the cropped square thumbnail in the card corner. */
  icon: string;
  themeClass: `theme-${AppId}`;
  gradientClass: `bg-${AppId}-gradient`;
  href: string;
}

const make = (
  id: AppId,
  name: string,
  community: string,
  tagline: string,
  description: string,
): AppMeta => ({
  id,
  name,
  community,
  tagline,
  description,
  logo: logoUrl(id),
  icon: iconUrl(id),
  themeClass: `theme-${id}`,
  gradientClass: `bg-${id}-gradient`,
  href: `/${id}`,
});

export const APPS: AppMeta[] = [
  make(
    "fryqnc",
    "Fryqnc",
    "LGBTQIA+ Community",
    "Find your frequency.",
    "A community app built for LGBTQIA+, trans, and non-binary members — connection, mentorship, and events on your terms.",
  ),
  make(
    "pulsynq",
    "Pulsynq",
    "Black Community",
    "Feel the pulse.",
    "A community app for the Black diaspora — culturally grounded matches, mentors, and gatherings rooted in our pulse.",
  ),
  make(
    "mindlynq",
    "MindLynq",
    "Neurodivergent Community",
    "Lynq your way.",
    "A community app for neurodivergent minds — sensory-aware design, clear communication tools, and the unique RESONATE feature.",
  ),
  make(
    "ablelynq",
    "Ablelynq",
    "Disability Community",
    "Connection without barriers.",
    "A community and connection platform for the disability community — accessibility-first design, peer support, and mentorship that meets you where you are.",
  ),
  make(
    "deafwav",
    "Deafwav",
    "Deaf & HoH Community",
    "Connection for the Deaf community.",
    "A community app for the Deaf and Hard of Hearing — visual-first communication, ASL-aware tools, and culturally grounded connection.",
  ),
  make(
    "fullynq",
    "Fullynq",
    "Plus-Size Community",
    "Show up fully.",
    "A community and connection platform for the plus-size community — body-affirming spaces, real visibility, and connection without compromise.",
  ),
  make(
    "jadesynq",
    "Jadesynq",
    "Pan-Asian Community",
    "Rooted. Resonant.",
    "A Pan-Asian community platform — cultural pride, intergenerational mentorship, and connection across the diaspora.",
  ),
  make(
    "openlynq",
    "Openlynq",
    "Open Relationship Community",
    "Connection, on your terms.",
    "A community app for open, poly, and ethically non-monogamous relationships — clear communication tools, consent-first design, and judgment-free space.",
  ),
  make(
    "spiritsynq",
    "Spiritsynq",
    "Faith & Spirituality Community",
    "Connection in spirit.",
    "A community app for faith and spirituality — reverent design, mentor-led guidance, and connection rooted in shared practice.",
  ),
];

export const WAVLYNQ_LOGO = logoUrl("wavlynq");

export const getApp = (id: AppId): AppMeta =>
  APPS.find((a) => a.id === id) ?? APPS[0];
